import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { AbstractControl, FormControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { differenceInMilliseconds, formatISO, parse } from 'date-fns';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

export function areMinutesValid(disabledMinutes: number[]): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }
    const minutes = control.value.split(':')[1];
    const isNotValid = disabledMinutes.includes(Number(minutes));
    return isNotValid ? { invalidValue: true } : null;
  };
}

@UntilDestroy()
@Component({
  selector: 'om-time-input',
  templateUrl: './om-time-input.component.html',
  styleUrls: ['./om-time-input.component.scss']
})
export class OmTimeInputComponent implements OnInit, OnChanges {
  @Input() control: FormControl = new FormControl();
  @Input() disabled: boolean = false;
  @Input() allowClear: boolean = true;

  @Input() label: string;
  @Input() icon: string;

  @Input() _class: string = 'w-full';

  @Input() placeholder = '';
  @Input() format = 'HH:mm';
  @Input() step = 1;
  @Input() hideDisabledOptions = false;
  @Input() disabledHours: () => number[] = () => [];
  @Input() disabledMinutes?: (hour?: number) => number[] = () => [];
  @Input() disabledSeconds?: (hour: number, minute: number) => number[] = () => [];

  isRequired$ = new BehaviorSubject<boolean>(false);
  _control = new FormControl();

  constructor() {}

  ngOnInit(): void {
    if (this.disabledMinutes().length !== 0) {
      this.control.addValidators(areMinutesValid(this.disabledMinutes()));
    }
    if (this.control.hasValidator(Validators.required)) {
      this.isRequired$.next(true);
      this._control.addValidators(Validators.required);
    }
    if (this.control.disabled) {
      this._control.disable();
    }
    this._control.setValue(this._parseTime(this.control.value));
    this._control.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      const timeString = value ? formatISO(value, { representation: 'time' }) : null;
      this.control.setValue(timeString);
      this.control.markAsDirty();
    });
    this.control.registerOnDisabledChange((isDisabled) => {
      if (isDisabled) this._control.disable({ emitEvent: false });
      else this._control.enable({ emitEvent: false });
    });
    this.control.registerOnChange((value) => {
      return this._control.setValue(this._parseTime(value), { emitEvent: false });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.control && !changes.control.firstChange) {
      if (changes.control.currentValue.hasValidator(Validators.required)) {
        this.isRequired$.next(true);
        this._control.addValidators(Validators.required);
      }
      if (changes.control.currentValue.disabled) {
        this._control.disable({ emitEvent: false });
      }
    }
  }

  private _parseTime(value: string) {
    if (!value) {
      return null;
    }
    if (value.length > 8) {
      value = value.substring(0, 8); //strip timezome
    }
    const time = parse(value, 'HH:mm:ss', new Date());
    return time;
  }
}
